import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
// InternalAxiosRequestConfig is extended below via module augmentation (no import needed)

import { StandardError, ApiClientConfig } from './types';

import { normalizeDuplicateApiPath } from '@/lib/api/normalizeDuplicateApiPath';
import { resolveProductionApiBaseUrl } from '@/lib/api/resolveApiBaseUrl';
import { invalidateStaleWorkspaceOnMismatch, isStaleWorkspaceRecoverySkipped } from '@/lib/workspaceOrgMismatch';
import { clearWorkspaceScopedClientState } from '@/state/workspaceClientState';
import { useWorkspaceStore } from '@/state/workspace.store';
import { getMemoryAccessToken, isAuthRefreshBypassUrl, isWorkspaceHeaderExemptPath, refreshSession } from '@/lib/api';
import { isCsrfForbidden, notifyNonCsrfForbidden } from '@/features/auth/accessDeniedSignal';
import { handleApiClient401 } from '@/lib/api/handleApiClient401';
import { ZEPHIX_BUILD_SHA } from '@/buildInfo';

function unwrapOneDataLayer(body: unknown): unknown {
  if (!body || typeof body !== 'object') return body;
  if ('data' in (body as Record<string, unknown>)) {
    return (body as Record<string, unknown>).data;
  }
  return body;
}


// Extend Axios types to include our custom metadata
declare module 'axios' {
  interface InternalAxiosRequestConfig {
    metadata?: { startTime: number };
  }
}

/**
 * Cross-origin (staging/prod): XSRF-TOKEN is stored for the API host; the SPA
 * origin cannot read it via document.cookie. We must cache the token from
 * GET /auth/csrf JSON (same as lib/api.ts) and send it as x-csrf-token; the
 * browser still sends the cookie on credentialed requests so the guard matches.
 */
export function clearApiClientCsrfCache(): void {
  apiClientInstance?.clearCsrfTokenMemory();
}

let apiClientInstance: ApiClient | null = null;

class ApiClient {
  private instance: AxiosInstance;
  private config: ApiClientConfig;
  /** Token from /auth/csrf body when cookie is not readable (cross-site API host). */
  private csrfTokenMemory: string | null = null;

  constructor() {
    this.config = {
      timeout: 10000,
      retries: 3,
      retryDelay: 1000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    };

    // Use Vite proxy in dev (/api -> localhost:3000), full URL in prod
    const baseURL = import.meta.env.PROD
      ? resolveProductionApiBaseUrl(import.meta.env.VITE_API_URL)
      : "/api"; // Relative path uses Vite proxy in development
    
    this.instance = axios.create({
      baseURL: baseURL,
      withCredentials: true,
    });
    this.setupInterceptors();
  }

  clearCsrfTokenMemory(): void {
    this.csrfTokenMemory = null;
  }

  /** Read the XSRF-TOKEN cookie set by the backend (same-origin only). */
  private getCsrfCookie(): string | null {
    const match = document.cookie
      .split('; ')
      .find((row) => row.startsWith('XSRF-TOKEN='));
    return match ? decodeURIComponent(match.split('=')[1]) : null;
  }

  /** Fetch a fresh CSRF token from the backend if cookie is missing */
  private csrfFetchPromise: Promise<string | null> | null = null;
  private async ensureCsrfToken(): Promise<string | null> {
    if (this.csrfTokenMemory) return this.csrfTokenMemory;

    const cookie = this.getCsrfCookie();
    if (cookie) {
      this.csrfTokenMemory = cookie;
      return cookie;
    }

    if (this.csrfFetchPromise) return this.csrfFetchPromise;

    this.csrfFetchPromise = (async () => {
      try {
        const baseURL = this.instance.defaults.baseURL || '/api';
        const res = await axios.get(`${baseURL}/auth/csrf`, { withCredentials: true });
        const body = res.data as { csrfToken?: string; token?: string } | undefined;
        const token = body?.csrfToken || body?.token || this.getCsrfCookie();
        if (token) this.csrfTokenMemory = token;
        return token || null;
      } catch {
        return null;
      } finally {
        this.csrfFetchPromise = null;
      }
    })();

    return this.csrfFetchPromise;
  }

  private static MUTATING_METHODS = ['post', 'put', 'patch', 'delete'];

  private setupInterceptors(): void {
    // Request interceptor
    this.instance.interceptors.request.use(
      async (config) => {
        const resolvedBase = String(config.baseURL ?? this.instance.defaults.baseURL ?? '');
        const normalized = normalizeDuplicateApiPath(config.url, resolvedBase);
        if (normalized !== undefined && normalized !== config.url) {
          config.url = normalized;
        }

        // Prefer in-memory Bearer when cookies are blocked cross-site (same as lib/api.ts).
        const access = getMemoryAccessToken();
        const reqUrl = config.url || '';
        if (access && !isAuthRefreshBypassUrl(reqUrl)) {
          config.headers['Authorization'] = `Bearer ${access}`;
        }

        // Add request ID for tracking
        config.headers['x-request-id'] = this.generateRequestId();

        // Add correlation ID for observability
        config.headers['x-correlation-id'] = this.generateCorrelationId();

        // Add timing metadata for telemetry
        config.metadata = { startTime: Date.now() };

        // Add organization header (stub for now)
        const orgId = this.getOrganizationId();
        if (orgId) {
          config.headers['x-organization-id'] = orgId;
        }

        // CSRF: attach token on every mutating request
        const url = config.url || '';
        const method = (config.method || 'get').toLowerCase();
        const isAuthEndpoint = url.includes('/api/auth') || url.includes('/auth/');
        const isHealthEndpoint = url.includes('/api/health') || url.includes('/health');

        if (ApiClient.MUTATING_METHODS.includes(method) && !isAuthEndpoint && !isHealthEndpoint) {
          const csrfToken = await this.ensureCsrfToken();
          if (csrfToken) {
            config.headers['x-csrf-token'] = csrfToken;
          }
        }

        // API-HEADER-ONE-PREDICATE-1: the one shared predicate (lib/api.ts) decides —
        // auth, health/version, org admin, org invites, users/me, my-tasks, gate review and
        // POST /workspaces carry no workspace header, a caller-supplied one included.
        // Any other route keeps a caller's header (ONE-TRUTH-FE-1), else gets the active one.
        const supplied = config.headers?.get?.('x-workspace-id');
        const callerNamedWorkspace = typeof supplied === 'string' && supplied.length > 0;

        if (isWorkspaceHeaderExemptPath(url, method)) {
          delete config.headers?.['X-Workspace-Id'];
          delete config.headers?.['x-workspace-id'];
        } else if (!callerNamedWorkspace) {
          // STEP D: Read activeWorkspaceId from Zustand store directly
          // This ensures we always have the latest value, not stale localStorage
          const wsId = useWorkspaceStore.getState().activeWorkspaceId;

          // STEP D: If activeWorkspaceId is null, delete headers to prevent stale context
          if (!wsId) {
            delete config.headers?.['X-Workspace-Id'];
            delete config.headers?.['x-workspace-id'];
          } else {
            // STEP D: Set header only when activeWorkspaceId exists
            config.headers = config.headers || {};
            config.headers['X-Workspace-Id'] = wsId;
            config.headers['x-workspace-id'] = wsId; // Support both cases
          }
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor
    this.instance.interceptors.response.use(
      (response: AxiosResponse) => {
        // Telemetry sampling (1% of requests)
        if (Math.random() < 0.01) {
          const endTime = Date.now();
          const startTime = response.config.metadata?.startTime || endTime;
          this.sendTelemetryMetric('http_timing', {
            url: response.config.url,
            method: response.config.method,
            status: response.status,
            duration: endTime - startTime,
            correlationId: response.config.headers?.['x-correlation-id'],
            requestId: response.config.headers?.['x-request-id'],
          });
        }
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & {
          _retry?: boolean;
          _csrfRetried?: boolean;
        };

        const status = error.response?.status;
        const data = error.response?.data as { code?: string; message?: string } | undefined;
        const isCsrfError =
          status === 403 &&
          (data?.code === 'CSRF_TOKEN_MISSING' ||
            data?.code === 'CSRF_TOKEN_MISMATCH' ||
            data?.code === 'CSRF_TOKEN_INVALID' ||
            /csrf/i.test(String(data?.message || '')));

        if (isCsrfError && originalRequest && !originalRequest._csrfRetried) {
          originalRequest._csrfRetried = true;
          this.clearCsrfTokenMemory();
          try {
            const fresh = await this.ensureCsrfToken();
            if (fresh) {
              originalRequest.headers = originalRequest.headers || {};
              (originalRequest.headers as Record<string, string>)['x-csrf-token'] = fresh;
              return this.instance(originalRequest as any);
            }
          } catch {
            /* fall through */
          }
        }

        // Handle 401 — attempt client-side refresh (shared with lib/api.ts), then retry.
        // Cookies alone are NOT sufficient on split SPA/API hosts; do not hard-redirect
        // before refresh (APICLIENT-401-NO-REFRESH-1).
        if (error.response?.status === 401) {
          return handleApiClient401({
            url: originalRequest.url ?? '',
            retried: Boolean(originalRequest._retry),
            refreshSession,
            retryRequest: async () => {
              originalRequest._retry = true;
              return this.instance(originalRequest as any);
            },
            onAuthFailure: () => this.handleAuthFailure(),
          });
        }

        // Stale workspace (403 mismatch today; 404 + WORKSPACE_NOT_FOUND after Step B):
        // invalidate the cached workspace before callers see the error. The helper decides
        // (WS-RECOVERY-404-1); pass the real status, never a hard-coded 403.
        // Skipped for requests about a non-active workspace (TR-2026-10-20-14c).
        const staleStatus = error.response?.status;
        if ((staleStatus === 403 || staleStatus === 404) && !isStaleWorkspaceRecoverySkipped(originalRequest)) {
          invalidateStaleWorkspaceOnMismatch({
            status: staleStatus,
            code: data?.code,
            message: data?.message,
            response: error.response,
          });
        }

        // Handle 403 - insufficient permissions
        if (error.response?.status === 403) {
          this.handlePermissionDenied();
          if (!isCsrfForbidden(data?.code, data?.message)) {
            notifyNonCsrfForbidden();
          }
        }

        // Handle 429 - rate limiting
        if (error.response?.status === 429) {
          const retryAfter = error.response.headers['retry-after'];
          if (retryAfter) {
            await this.delay(parseInt(retryAfter) * 1000);
            return this.instance(originalRequest);
          }
        }

        // Convert to standardized error format
        const standardError = this.normalizeError(error);
        return Promise.reject(standardError);
      }
    );
  }

  // Removed getAuthToken - using cookies only

  private getOrganizationId(): string | null {
    // This will be connected to the organization context later
    return localStorage.getItem('organization_id');
  }

  private getWorkspaceId(): string | null {
    // Get workspace ID from workspace store (workspace-storage)
    try {
      const workspaceStorage = localStorage.getItem('workspace-storage');
      if (workspaceStorage) {
        const { state } = JSON.parse(workspaceStorage);
        return state?.activeWorkspaceId || null;
      }
    } catch (error) {
      console.warn('Failed to get workspace ID from storage:', error);
    }
    return null;
  }

  private generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private generateCorrelationId(): string {
    return `corr_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private sendTelemetryMetric(metricName: string, data: Record<string, any>): void {
    // In production, send to your monitoring service
    if (import.meta.env.PROD) {
      // TODO: Replace with your actual telemetry service
      // Example: send to DataDog, New Relic, or custom endpoint
      console.log(`[TELEMETRY] ${metricName}:`, {
        ...data,
        timestamp: new Date().toISOString(),
        buildTag: import.meta.env.VITE_BUILD_TAG,
        gitHash: ZEPHIX_BUILD_SHA,
      });

      // Example implementation:
      // apiClient.post('/telemetry', { metric: metricName, data }).catch(() => {}); // Silent fail
    }
  }

  // Session refresh: see refreshSession() in lib/api.ts (shared with this client).

  private handleAuthFailure(): void {
    // Cleanup any legacy tokens dynamically to avoid circular deps
    import('@/auth/cleanupAuthStorage').then(({ cleanupLegacyAuthStorage }) => {
      cleanupLegacyAuthStorage();
    }).catch(() => { /* ignore cleanup failures */ });

    // Token expiry / forced login redirect — same workspace clear as AuthContext.logout.
    clearWorkspaceScopedClientState();

    // Don't redirect if we're on an admin route - let AdminRoute handle it
    const isAdminRoute = window.location.pathname.startsWith('/admin');
    if (!isAdminRoute) {
      // Redirect to login page
      window.location.href = '/login';
    }
  }

  private handlePermissionDenied(): void {
    // Don't redirect - let the component handle 403 errors
    // This allows admin pages to show inline error messages
    // instead of redirecting away from the admin console
    console.warn('Insufficient permissions (403)');
    // The error will be caught by the component's error handler
  }

  private normalizeError(error: AxiosError): StandardError {
    const status = error.response?.status || 500;
    const errorData = error.response?.data as { message?: string; code?: string } | undefined;
    const message = errorData?.message || error.message || 'An error occurred';

    let code: StandardError['code'] = 'SERVER_ERROR';
    // Preserve backend AppException codes (e.g. AUTH_FORBIDDEN on workspace mismatch).
    if (typeof errorData?.code === 'string' && errorData.code.trim()) {
      code = errorData.code as StandardError['code'];
    } else if (status === 400) code = 'VALIDATION_ERROR';
    else if (status === 401) code = 'AUTH_ERROR';
    else if (status === 404) code = 'NOT_FOUND';
    else if (status >= 500) code = 'SERVER_ERROR';
    else if (!navigator.onLine) code = 'NETWORK_ERROR';

    return {
      code,
      message,
      status,
      timestamp: new Date().toISOString(),
      requestId: error.config?.headers?.['x-request-id'] as string,
    };
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Public API methods
  async get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<any> {
    const response = await this.instance.get(url, config);
    return unwrapOneDataLayer(response.data) as T;
  }

  async post<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<any> {
    const response = await this.instance.post(url, data, config);
    return unwrapOneDataLayer(response.data) as T;
  }

  async put<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<any> {
    const response = await this.instance.put(url, data, config);
    return unwrapOneDataLayer(response.data) as T;
  }

  async patch<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<any> {
    const response = await this.instance.patch(url, data, config);
    return unwrapOneDataLayer(response.data) as T;
  }

  async delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<any> {
    const response = await this.instance.delete(url, config);
    return unwrapOneDataLayer(response.data) as T;
  }

  // Removed token utility methods - using cookies only

  setOrganizationId(orgId: string): void {
    localStorage.setItem('organization_id', orgId);
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
apiClientInstance = apiClient;
export default apiClient;
